// Resolve an admin uuid to a human-readable display name. Wraps the
// shared `useAdminsListQuery` (single TanStack key, deduped across
// callers) + a uuid→name lookup map + a resolver function that handles
// common edge cases (empty / "System" / unknown uuid → short tag).
//
// Containers only (per architecture-principles §1) — this hook calls
// a query underneath. Presentational components receive the resolved
// string via props, not the resolver function.

import { computed } from 'vue'

import { useAdminsListQuery } from '../api/queries/useAdminsListQuery'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isUuidLike(value: string): boolean {
  return UUID_RE.test(value)
}

export interface UseAdminNameResolverReturn {
  adminNameById: ReturnType<typeof computed<Map<string, string>>>
  /**
   * Resolve a raw actor string (admin uuid, "System", or arbitrary
   * label) to a display name. Returns null when the input is empty;
   * otherwise:
   *   - "System" → "System"
   *   - matched admin uuid → admin's display name
   *   - unknown uuid → "Admin · XXXXXX" (last 6 chars uppercased)
   *   - anything else → returned verbatim
   */
  resolveAdminName: (raw: string | null | undefined) => string | null
}

export function useAdminNameResolver(): UseAdminNameResolverReturn {
  // High limit — covers typical internal admin pools. If the team grows
  // past 100, bump here or add paging at consumers.
  const adminsQuery = useAdminsListQuery(() => ({ page: 1, limit: 100 }))

  const adminNameById = computed<Map<string, string>>(() => {
    const map = new Map<string, string>()
    for (const admin of adminsQuery.data.value?.data ?? []) {
      if (admin.id) map.set(admin.id, admin.name || admin.email || admin.id)
    }
    return map
  })

  function resolveAdminName(raw: string | null | undefined): string | null {
    if (!raw) return null
    if (raw === 'System') return 'System'
    const mapped = adminNameById.value.get(raw)
    if (mapped) return mapped
    if (isUuidLike(raw)) return `Admin · ${raw.slice(-6).toUpperCase()}`
    return raw
  }

  return { adminNameById, resolveAdminName }
}
